import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Maximize, X } from "lucide-react";

// Full-screen view for a story's video or storybook window. The window itself
// animates from where it sits on the page to the middle of the screen, sized to
// fit while keeping its own proportions. The page behind it darkens slowly.
// On a phone held upright the window is turned to landscape. The same element
// stays mounted the whole time, so the video keeps playing and the storybook
// keeps its page.
//
// Animates only position, size and opacity, on one element, so it stays smooth
// on low-end phones. Respects "reduce motion" by skipping the animation.

const MARGIN = 12;
const OPEN_MS = 700;
const EASE = "cubic-bezier(0.22, 0.8, 0.24, 1)";
const BACKDROP_ALPHA = 0.9;

function reduceMotion() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// A phone or tablet held upright. It gets the landscape turn; a desktop never does.
function wantsLandscapeTurn() {
  const touch = window.matchMedia("(pointer: coarse)").matches || navigator.maxTouchPoints > 0;
  return window.innerHeight > window.innerWidth && touch;
}

// Largest box with the given aspect ratio that fits inside (availW x availH).
function fit(aspect, availW, availH) {
  let w = availW;
  let h = w / aspect;
  if (h > availH) {
    h = availH;
    w = h * aspect;
  }
  return { w, h };
}

// Box for the window's full-screen position. When it is turned, the box is laid
// out sideways and rotated 90deg about its centre, so its visible width is the
// screen height and its visible height is the screen width.
function fullscreenBox(aspect) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const turned = wantsLandscapeTurn();
  const availW = (turned ? vh : vw) - MARGIN * 2;
  const availH = (turned ? vw : vh) - MARGIN * 2;
  const { w, h } = fit(aspect, availW, availH);
  return {
    position: "fixed",
    left: (vw - w) / 2,
    top: (vh - h) / 2,
    width: w,
    height: h,
    transform: turned ? "rotate(90deg)" : "none",
    borderRadius: 16,
    zIndex: 1001,
    overflow: "hidden",
  };
}

export default function FullscreenFrame({ children, testid, label = "Full screen" }) {
  const slotRef = useRef(null);
  const stageRef = useRef(null);
  const phaseRef = useRef("idle"); // idle | opening | open | closing
  const aspectRef = useRef(16 / 9);
  const [phase, setPhase] = useState("idle");
  const [stageStyle, setStageStyle] = useState(null); // null = normal page flow
  const [slotHeight, setSlotHeight] = useState(null);

  const timerRef = useRef(null);
  const armFallback = (fn) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(fn, OPEN_MS + 150);
  };
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const setPhaseBoth = (next) => {
    phaseRef.current = next;
    setPhase(next);
  };

  const finish = useCallback(() => {
    setPhaseBoth("idle");
    setStageStyle(null);
    setSlotHeight(null);
    document.body.style.overflow = "";
  }, []);

  const open = () => {
    const slot = slotRef.current;
    if (!slot || phaseRef.current !== "idle") return;
    const r = slot.getBoundingClientRect();
    // Fit the window to the book itself, not the full-width slot around it.
    const book = stageRef.current?.firstElementChild?.getBoundingClientRect();
    aspectRef.current = book && book.height ? book.width / book.height : r.width / r.height;
    setSlotHeight(r.height);
    document.body.style.overflow = "hidden";
    setPhaseBoth("opening");

    // Start exactly where the window sits now, with no animation.
    setStageStyle({ position: "fixed", left: r.left, top: r.top, width: r.width, height: r.height, transform: "none", borderRadius: 0, zIndex: 1001, overflow: "hidden", transition: "none" });

    // Real full screen hides the browser bars. Some browsers refuse it, so ignore failures.
    // Android Chrome only allows the landscape lock once full screen has started,
    // so the lock waits for that. Where it is not allowed, the window is turned by CSS.
    const entering = document.documentElement.requestFullscreen?.();
    if (entering?.then && wantsLandscapeTurn()) {
      entering
        .then(() => window.screen?.orientation?.lock?.("landscape"))
        .catch(() => {});
    } else {
      entering?.catch?.(() => {});
    }

    const target = fullscreenBox(aspectRef.current);
    if (reduceMotion()) {
      setStageStyle(target);
      setPhaseBoth("open");
      return;
    }
    // Two frames, so the browser has painted the start position before it moves.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      setStageStyle({ ...fullscreenBox(aspectRef.current), transition: `all ${OPEN_MS}ms ${EASE}` });
    }));
    armFallback(() => { if (phaseRef.current === "opening") setPhaseBoth("open"); });
  };

  const close = useCallback(() => {
    if (phaseRef.current !== "open" && phaseRef.current !== "opening") return;
    const slot = slotRef.current;
    const r = slot.getBoundingClientRect();
    setPhaseBoth("closing");
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    try { window.screen?.orientation?.unlock?.(); } catch { /* not supported */ }
    if (reduceMotion()) {
      finish();
      return;
    }
    setStageStyle((s) => ({
      ...s,
      left: r.left, top: r.top, width: r.width, height: r.height,
      transform: "none", borderRadius: 0,
      transition: `all ${OPEN_MS}ms ${EASE}`,
    }));
    armFallback(() => { if (phaseRef.current === "closing") finish(); });
  }, [finish]);

  // Keep the window fitted if the screen size or orientation changes while open.
  useEffect(() => {
    const onResize = () => {
      if (phaseRef.current !== "open" && phaseRef.current !== "opening") return;
      setStageStyle({ ...fullscreenBox(aspectRef.current), transition: "none" });
    };
    // Pressing Esc in the browser's own full screen leaves it; close our view too.
    const onFsChange = () => {
      if (!document.fullscreenElement && (phaseRef.current === "open" || phaseRef.current === "opening")) close();
    };
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("resize", onResize);
    document.addEventListener("fullscreenchange", onFsChange);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", onResize);
      document.removeEventListener("fullscreenchange", onFsChange);
      window.removeEventListener("keydown", onKey);
    };
  }, [close]);

  // Leaving the page while full screen restores everything.
  useEffect(() => () => {
    document.body.style.overflow = "";
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
  }, []);

  const onStageTransitionEnd = (e) => {
    if (e.target !== stageRef.current || e.propertyName !== "width") return;
    if (phaseRef.current === "opening") setPhaseBoth("open");
    if (phaseRef.current === "closing") finish();
  };

  const overlay = phase !== "idle" && (
    <>
      <div
        className="fixed inset-0 bg-black"
        style={{
          zIndex: 1000,
          opacity: phase === "closing" ? 0 : BACKDROP_ALPHA,
          transition: reduceMotion() ? "none" : `opacity ${OPEN_MS + 200}ms ease-out`,
        }}
        aria-hidden="true"
      />
      <button
        type="button"
        onClick={close}
        data-testid={testid ? `${testid}-exit` : undefined}
        className="fixed flex items-center gap-2 rounded-full bg-red-600 px-4 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-red-700 active:scale-95"
        style={{
          zIndex: 1002,
          top: "calc(12px + env(safe-area-inset-top))",
          right: 12,
          opacity: phase === "closing" ? 0 : 1,
          transition: reduceMotion() ? "none" : `opacity ${OPEN_MS}ms ease-out`,
        }}
      >
        <X size={18} strokeWidth={2.6} aria-hidden="true" /> Exit full screen
      </button>
    </>
  );

  return (
    <>
      <div className="flex justify-end mb-2 min-h-[40px]">
        {phase === "idle" && (
          <button
            type="button"
            onClick={open}
            data-testid={testid ? `${testid}-open` : undefined}
            className="hv-btn-secondary inline-flex items-center gap-2 px-4 py-2 text-sm min-h-[40px]"
          >
            <Maximize size={16} strokeWidth={2.4} aria-hidden="true" /> {label}
          </button>
        )}
      </div>
      <div ref={slotRef} style={{ position: "relative", minHeight: slotHeight ?? undefined }}>
        <div
          ref={stageRef}
          style={stageStyle ?? { position: "relative", width: "100%" }}
          onTransitionEnd={onStageTransitionEnd}
          data-testid={testid}
        >
          {children}
        </div>
      </div>
      {overlay && createPortal(overlay, document.body)}
    </>
  );
}
